import { Button, CircularProgress } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { ErrorOutline, HourglassEmpty, Print } from '@material-ui/icons';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNotify } from 'react-admin';
import {
  useQuizAssessmentTestDataProvider
} from "../../../providers/data-provider/quiz-assessment-test.data.provider";

const POLLING_INTERVAL_MS = 3000;
const FINAL_STATUSES = ['done', 'error'];

const useStyles = makeStyles(() => ({
  pendingButton: {
    '&.Mui-disabled': {
      borderColor: '#9e9e9e',
      color: '#9e9e9e',
    },
  },
  processingButton: {
    '&.Mui-disabled': {
      borderColor: '#ff9800',
      color: '#ff9800',
    },
  },
  errorButton: {
    borderColor: '#f44336',
    color: '#f44336',
  },
}));

export function PrintClassTest({ availabilityId, assessmentName, variant = "outlined", color = "default", size = "small" }) {
  const classes = useStyles();
  const { printClassTest, pubPrintClassTest, getTestDocumentByAvailability } = useQuizAssessmentTestDataProvider();
  const [printing, setPrinting] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [status, setStatus] = useState();
  const pollingRef = useRef(null);
  const notify = useNotify();

  useEffect(() => {
    getTestDocumentByAvailability(availabilityId)
        .then(({ data }) => {
          const nextStatus = data?.status;
          if (!nextStatus) return;
          setStatus(nextStatus);
        })
        .catch(() => {});
  }, [availabilityId]);

  const stopPolling = useCallback(() => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
  }, []);

  const startPolling = useCallback(() => {
    stopPolling();
    let fetching = false;
    pollingRef.current = setInterval(() => {
      if (fetching) return;
      fetching = true;
      getTestDocumentByAvailability(availabilityId)
        .then(({ data }) => {
          const nextStatus = data?.status;
          if (!nextStatus) return;
          setStatus(nextStatus);
          if (FINAL_STATUSES.includes(nextStatus)) {
            stopPolling();
          }
        })
        .catch(() => {})
        .finally(() => { fetching = false; });
    }, POLLING_INTERVAL_MS);
  }, [getTestDocumentByAvailability, availabilityId, stopPolling]);

  useEffect(() => stopPolling, [stopPolling]);

  const handleDownload = () => {
    setDownloading(true);
    printClassTest(availabilityId, assessmentName).finally(() => setDownloading(false))
        .catch(error => {
          notify('Avaliação não disponível', 'error');
        });
  };

  const pubPrintRequest = () => {
    setPrinting(true);
    startPolling();
    setStatus('processing');
    pubPrintClassTest(availabilityId).finally(() => setPrinting(false));
  };

  if (status === 'done') {
    return (
        <Button
            variant={variant}
            size={size}
            className={classes.pendingButton}
            startIcon={downloading ? <CircularProgress size={16} color="inherit" /> : <Print />}
            onClick={handleDownload}
        >
          Baixar Avaliação da Turma
        </Button>
    );
  }

  if (status === 'processing') {
    return (
        <Button
            variant={variant}
            size={size}
            className={classes.processingButton}
            startIcon={<CircularProgress size={16} color="inherit" />}
            disabled
        >
          Provas da Turma Sendo Gerada
        </Button>
    );
  }

  if (status === 'error') {
    return (
        <Button
            variant={variant}
            size={size}
            className={classes.errorButton}
            startIcon={<ErrorOutline />}
            disabled
        >
          Erro na Geração
        </Button>
    );
  }

  return (
      <Button
          variant={variant}
          color={color}
          size={size}
          startIcon={downloading ? <CircularProgress size={16} color="inherit" /> : <HourglassEmpty />}
          onClick={pubPrintRequest}
          disabled={downloading}
      >
          Gerar Avaliação da Turma
      </Button>
  );
}